import { Component, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { withFormik, Form } from "formik";
import { Formik } from "formik";

import LoadingDialog from "../Utils/LoadingDialog";
import SelfHelp from "../Utils/SelfHelp";
import { ZonesTable, NetworkTable, PhenologyTable } from "./Tables";
import { callSend, countriesList, AreaRow, FormRow, RawRow, SelectRow, HeaderButtons, Vinculos } from "../Form/Form";
import { GoogleMapsFarm } from "../GoogleMapsFarm/GoogleMapsFarm";

import { faInfoCircle, faPlus, faFileImport, faFileExport, faNetworkWired } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dateFormat } from "../../utils/date";

import "react-toastify/dist/ReactToastify.css";
import "../Form/FormStyles.css";
import "./FarmInfo.css";

import { FarmContext } from "../../contexts/FarmContext";
import { getGMT } from "../../utils/date";

import getBaseLangs from "../../utils/language";
import withName from "../../hoc/withName";
import { toast } from "react-toastify";

import { RestAPI } from "@aws-amplify/api-rest";
import DatosFact from "../BillingPolicyAndTerms/DatosFact";
import BillingParamsPanel from "../BillingParams/BillingParamsPanel";
import TooltipPrime from "../PrimeComponents/TooltipPrime";
import DropdownPrime from "../PrimeComponents/DropdownPrime";
import { escapeXML } from "../../utils/DataUtils";

const farm = "farm_form";
class FarmForm extends Component {
    constructor(props) {
        super(props);
        this.prefijoOptions = this.getOptions(props);
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.t !== nextProps.t) {
            this.prefijoOptions = this.getOptions(nextProps);
        }
        return true;
    }

    getOptions({ t }) {
        return [
            { value: "txt.Campo_NOMBRE", label: t("txt.Campo_LABEL") },
            { value: "txt.Fundo_NOMBRE", label: t("txt.Fundo_LABEL") },
            { value: "txt.Vacio_NOMBRE", label: t("txt.Vacio_LABEL") }
        ];
    }

    render() {
        const { resetForm, status = {}, canEdit, setStatus, data, t, handleSubmit, i18n, redirDC, adminUsers } = this.props;
        if (!data) return <div className="card col-xl-6 col-lg-12 col-sm-12">No Data</div>;
        const isEdit = canEdit && status.edit;
        let locationText = null, locationWarning = null;
        if (data.location) {
            const { county, region, latitud, longitud } = data.location;
            locationText = `${county || ""}, ${region || ""}`;
            if (latitud !== data.latitud || longitud !== data.longitud) {
                locationWarning = "farms.oldLocation";
            }
        }

        const SelfHelpFarminfo1 = import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-1.png`];
        const SelfHelpFarminfo2 = import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-2.png`];
        const selfHelpParams = {
            header: t("selfHelp.farmInfo.properties"),
            params: [t("farms.prefijo"), t("area.area")],
            definitionName: [
                t("farms.prefijo"),
                t("area.area"),
                t("area.calculated")
            ],
            definitionText: [t("selfHelp.definitions.prefix"), t("selfHelp.definitions.area"), t("selfHelp.definitions.estimatedArea")]
        };

        return (
            <div className="card">
                <div className="card-body altura-min">
                    <Form id={farm} onSubmit={handleSubmit}>
                        <div className='row'>
                            <div className='col col-sm-2 col-md-3 col-lg-2'>
                                <SelfHelp t={t} params={selfHelpParams} images={[SelfHelpFarminfo1, SelfHelpFarminfo2]} lng={i18n.language} />
                            </div>
                            <div className='col col-sm-8 col-md-6 col-lg-8 text-center'>
                                <h5 className="card-title">
                                    <span>{(data && data.prefijo) ? `${t(data.prefijo.replace("NOMBRE", "INFO"))}` : t("txt.Vacio_INFO")}</span>
                                </h5>
                            </div>
                            <div className='col col-sm-2 col-md-3 col-lg-2 d-flex justify-content-end align-items-center'>
                                <HeaderButtons edit={status.edit} canEdit={canEdit} setStatus={setStatus} resetForm={resetForm} form={farm} />
                            </div>
                        </div>
                        <div className='linea-titulo' />
                        <div className='row'>
                            <div className='col-12 text-center'>
                                <SelectRow edit={isEdit}
                                    nombre={t("farms.prefijo")}
                                    campo="prefijo"
                                    dataValue={data.prefijo}
                                    defaultText="..."
                                    options={this.prefijoOptions}
                                    optionKey="value"
                                    optionLabel="label"
                                    placeholder={t("farms.prefijo")}
                                />
                                <FormRow
                                    dato1={data.nombre}
                                    nombre1={t("general.name")}
                                    campo1="nombre"
                                    dato2={data.razon_social}
                                    nombre2={t("farms.farmTable.razonSocial")}
                                    campo2="razon_social"
                                />
                                <FormRow
                                    dato1={data.fecha_creacion === "" || data.fecha_creacion === null ? null : dateFormat(data.fecha_creacion, t, i18n.language, data.huso_horario, true)}
                                    nombre1={t("farms.fechaCreacion")}
                                    campo1="fecha_creacion"
                                />
                                <FormRow
                                    dato1={adminUsers ? adminUsers : "-"}
                                    nombre1={t("farms.admins")}
                                    campo1="administradores_campo"
                                />
                                <RawRow
                                    dato1={locationText}
                                    nombre1={t("personal.location")}
                                    alert1={locationWarning && t(locationWarning)}
                                    dato2={countriesList.getName(data.pais, getBaseLangs(i18n)[0])}
                                    nombre2={t("farms.farmTable.countryFarm")}
                                />
                                <FormRow
                                    dato1={`${data.huso_horario} ${data.gmt}`}
                                    nombre1={t("farms.farmTable.timeZone")}
                                    campo1="huso_horarioGMT"
                                />
                                <AreaRow
                                    edit={isEdit}
                                    dato1={t("general.number_unit", { value: { number: data.superficie, unit: data.unidadArea } })}
                                    dato2={t("general.number_unit", { value: { number: data.calculatedArea, unit: data.unidadArea } })}
                                    nombre1={t("area.area")}
                                    nombre2={t("area.calculated")}
                                    campo1="superficie"
                                    unit1={data.unidadArea}
                                />
                                <FormRow
                                    edit={isEdit && data.mapa_json}
                                    dato1={data.mapa_json ? data.mapa_json.configurado ? t("general.answerYes") : t("general.answerNo") : t("general.answerNo")}
                                    nombre1={t("forms.mostrarMapa")}
                                    campo1="configurado"
                                    type1="checkbox"
                                />
                                <Vinculos farmDC={redirDC} gMapPosition={(data.latitud && data.longitud) ? `https://maps.google.com/?q=${data.latitud},${data.longitud}` : null} />

                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}

const FormikForm = withFormik({
    mapPropsToStatus: (props) => {
        return {
            edit: (props && props.edit) || false,
        };
    },
    mapPropsToValues: (props) => {
        const data = Object.assign({}, props.data);
        return {
            prefijo: data.prefijo,
            superficie: data.superficie,
            mapa_json: data.mapa_json,
            configurado: data.mapa_json ? data.mapa_json.configurado : null
        };
    },
    enableReinitialize: true,
    handleSubmit: (values, { props, ...actions }) => {
        props.updateData(values, props);
        actions.setStatus({
            edit: false,
        });
    }
})(withTranslation()(FarmForm));

const FarmInfo = ({ dataFarm, setDataFarm, mapRef, isLoaded }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location && location.search);
    const farmblack = params.get("farmblack") || null;
    if (params.get("fail")) dataFarm.failPls();

    const { t, i18n } = useTranslation();
    const [farmId, setFarmId] = useState("");
    const [accountId, setAccountId] = useState("");

    const [_, dispatch] = useContext(FarmContext);
    const sectorsInfo = dispatch({ type: "get", path: "sectors" });
    const network = dispatch({ type: "get", path: "network" });
    const equiposDict = dispatch({ type: "get", path: "pumpsysDict" });
    const sectorsDict = dispatch({ type: "get", path: "sectorsDict" });
    const nodesDict = dispatch({ type: "get", path: "nodesDict" });
    const canEdit = dispatch({ type: "get", path: "canEdit" });
    const units = dispatch({ type: "get", path: "units" });
    const users = dispatch({ type: "get", path: "users" });
    const fuentesCoords = dispatch({ type: "get", path: "fuentes_coords" });
    const plantillasFenologia = dispatch({ type: "get", path: "fenologia" });
    const tooltipVisible = (!dataFarm.mapa_json || !dataFarm.mapa_json.position || !dataFarm.mapa_json.bounds) ? true : false;
    const [dialogState, setDialogState] = useState({ visible: false });
    const hasDownlodableMap = () => {
        if (dataFarm.mapa_json) {
            return (fuentesCoords.length > 0
        || (dataFarm?.mapa_json?.sectores && Object.keys(dataFarm.mapa_json.sectores).length > 0)
        || (dataFarm?.mapa_json?.nodos && Object.keys(dataFarm.mapa_json.nodos).length > 0));
        } else {
            return fuentesCoords.length > 0;
        }
    };

    const enableDownloadKML = hasDownlodableMap();


    const SelfHelpFarminfo1 = import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-zonetable-simple-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-zonetable-simple-1.png`];
    const SelfHelpFarminfo2 = import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-zonetable-simple-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-zonetable-simple-2.png`];
    const selfHelpParams = {
        header: t("selfHelp.farmInfo.properties"),
        paramsGeneral: [
            t("general.name"),
            t("farms.zone.nombreAlternativo"),
            t("area.area"),
            t("cultivos.especie"),
            t("cultivos.variedad"),
            t("cultivos.subespecie"),
            t("farms.zone.kc"),
        ],
        paramsRiego: [t("farms.zone.caudalTeorico")],
        paramsSuelo: [t("farms.zone.maximo"), `${t("farms.zone.puntoCritico")} 1`, `${t("farms.zone.puntoCritico")} 2`,
            `${t("farms.zone.puntoCritico")} 3`, `${t("farms.zone.puntoCritico")} 4`, t("farms.zone.minimo")],
        paramsClima: [t("farms.zone.calorLimite"), `${t("farms.zone.puntoGestion")} 1`, `${t("farms.zone.puntoGestion")} 2`, t("farms.zone.heladaLimite")],
        definitionName: [
            t("area.area"),
            t("area.calculated"),
            t("farms.zone.kc"),

            t("farms.zone.caudalTeorico"),

            t("farms.zone.maximo"),
            t("selfHelp.definitionsNames.puntosCriticos"),
            t("farms.zone.minimo"),

            t("farms.zone.calorLimite"),
            t("selfHelp.definitionsNames.temperaturas"),
            t("farms.zone.heladaLimite")
        ],
        definitionText: [
            t("selfHelp.definitions.area"),
            t("selfHelp.definitions.estimatedArea"),
            t("selfHelp.definitions.kc"),
            t("selfHelp.definitions.caudalTeorico"),
            t("selfHelp.definitions.maximo"),
            t("selfHelp.definitions.puntosCriticos"),
            t("selfHelp.definitions.minimo"),
            t("selfHelp.definitions.calor"),
            t("selfHelp.definitions.temperaturas"),
            t("selfHelp.definitions.heladas"),

        ]
    };

    const selfHelpNetImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-netTable-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-netTable-1.png`];
    const selfHelpNetImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-netTable-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-netTable-2.png`];

    const selfHelpParamsNet = {
        header: t("selfHelp.farmInfo.properties"),
        params: [`${t("general.name")} (${t("selfHelp.node.ifIsGwMsg")})`, t("node.latitud"), t("node.longitud")],
        definitionName: [
            `${t("node.latitud")}/${t("node.longitud")}`,
            t("node.ultimoDato"),
            t("node.tipoPlan")
        ],
        definitionText: [t("selfHelp.definitions.latLng"), t("selfHelp.definitions.lastDate"), t("selfHelp.definitions.planType")]
    };

    const phenologyHelpImages = {
        phenologyTable: import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-fenologia.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-fenologia.png`],
        deleteDialog: import.meta.glob("../../img/autoayuda/*/autoayuda-farminfo-fenologia-delDialog.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-farminfo-fenologia-delDialog.png`]
    };
    useEffect(() => {
        if (dataFarm) {
            setFarmId(dataFarm.campo_id);
            setAccountId(dataFarm.cuenta_id);
        }
    }, [dataFarm]);

    const formikRef = useRef(null);
    const [loadedDatosFact,setLoadedDatosFact] = useState(true);
    const [billingData,setBillingData] = useState(true);
    const [billingInfoFarm, setBillingInfoFarm] = useState(null);
    const [countries, setCountries] = useState(null);
    const [originalValues, setOriginalValues] = useState(null);
    const [onEditFact, setOnEditFact] = useState(false);
    const [onSendDatosFact, setOnSendDatosFact] = useState(false);
    const [newDatosFact,setNewDatosFact] = useState(null);
    const [validate, setValidate] = useState(false);
    const [newDatosFactList, setNewDatosFactList] = useState(null);
    useEffect(() => {
        setLoadedDatosFact(false);
        setBillingInfoFarm(null);
        setCountries(null);
        setOriginalValues(null);
        setNewDatosFact(null);
        if (farmId) {
            RestAPI.get("apiAdminDropControl","/billing/agreements",
                {queryStringParameters: {"farmId":dataFarm.campo_id}}).then(async (response) => {
                const farmInfo = response?.[0]?.result?.farmInfo?.[0];
                if(farmInfo && farmInfo?.n_cliente_id){
                    setBillingInfoFarm(farmInfo);
                    setCountries(response?.[0]?.result?.countries);
                    formikRef.current && formikRef.current.setFieldValue("n_cliente_id",farmInfo?.n_cliente_id);
                    formikRef.current && formikRef.current.setFieldValue("datos_fact",farmInfo?.datos_fact);
                    setBillingData(farmInfo?.datos_fact);
                    setLoadedDatosFact(true);
                }
            })
                .catch((error) => {
                    console.log(error);
                });
        }
    }, [farmId]);

    useEffect(() => {
        if(formikRef.current && validate){
            formikRef.current.validateForm();
            setValidate(false);
        }
    }, [validate]);
    //const GMT = getGMT(dataFarm && dataFarm.huso_horario);
    dataFarm.gmt = getGMT(dataFarm && dataFarm.huso_horario);
    //https://www.dropcontrol.com/site/dashboard/campo.do?cuentaId=273&campoId=531
    const redirDC = farmId !== "" && accountId !== "" && `https://www.dropcontrol.com/site/dashboard/campo.do?cuentaId=${accountId}&campoId=${farmId}`;

    const accountUsers = users.data ? users.data.users : null;

    const adminUsers = useMemo(() => {
        let nombres = "";
        if (accountUsers !== null && Array.isArray(accountUsers) && accountUsers.length > 0) {
            accountUsers.forEach(user => {
                if (user.is_admin) {
                    const userName = `${user.nombre} ${user.apellido_paterno}`;
                    nombres = `${nombres !== "" ? `${nombres},` : ""} \n${userName}`;
                }
            });
            return nombres === "" ? null : nombres;
        }

        return nombres === "" ? null : nombres;
    }, [accountUsers]);

    const redirectToMapaConfig = () => {
        const newSearch = `?farm=${farmId}`;
        navigate({ pathname: "/farms/mapconfig", search: newSearch });
    };

    const exportToKML = async () => {
        try {
            if (enableDownloadKML) {
                const mapa_json = dataFarm?.mapa_json;
                const filetype = "application/vnd.google-earth.kml+xml";
                const filename = dataFarm.nombre + ".kml";
                let kmlData = `<?xml version="1.0" encoding="UTF-8"?>
    <kml xmlns="http://www.opengis.net/kml/2.2"> <Document>
    <Style id="s1">
      <LineStyle>
        <color>7f00ff00</color>
        <width>4</width>
      </LineStyle>
      <PolyStyle>
        <color>7f00ff00</color>
        <colorMode>normal</colorMode>
        <fill>1</fill>
        <outline>1</outline>
      </PolyStyle>
    </Style>
    <name>${escapeXML(dataFarm.nombre)}</name>\n`;

                if (mapa_json) {

                    if (mapa_json?.sectores && Object.keys(mapa_json.sectores).length > 0) {
                        kmlData += `<Folder>
      <name>${t("mapconfig.sectores")}</name>\n`;
                        for (const sectorId of Object.keys(mapa_json.sectores)) {
                            const sector = sectorsDict[sectorId];
                            if (sector) {
                                let kmlPolygonData = `<Placemark>
          <styleUrl>#s1</styleUrl>
          <name>${escapeXML(sector.nombre)}</name>
          <Polygon>
          <outerBoundaryIs>
          <LinearRing>
          <coordinates>\n`;
                                mapa_json.sectores[sectorId].path.forEach((s) => {
                                    //feature.geometry.coordinates.push([s.lng, s.lat]);
                                    kmlPolygonData += `${s.lng},${s.lat},0\n`;
                                });

                                kmlPolygonData += `${mapa_json.sectores[sectorId].path[0].lng},${mapa_json.sectores[sectorId].path[0].lat},0\n`;

                                kmlPolygonData += `</coordinates>
          </LinearRing>
          </outerBoundaryIs>
          </Polygon>
          </Placemark>\n`;

                                kmlData += kmlPolygonData;
                            }

                        }
                        kmlData += "</Folder>\n";
                    }

                    if (mapa_json?.nodos && Object.keys(mapa_json.nodos).length > 0) {
                        kmlData += `<Folder>
      <name>${t("mapconfig.nodos")}</name>\n`;
                        for (const nodoId of Object.keys(mapa_json.nodos)) {
                            const nodo = nodesDict[nodoId];
                            if (nodo) {
                                const kmlPlacemarkData = `<Placemark>
        <name>${escapeXML(nodo.nombre)}</name>
        <Point>
        <coordinates>${mapa_json.nodos[nodoId].lng},${mapa_json.nodos[nodoId].lat},0</coordinates>
        </Point>
        </Placemark>\n`;
                                kmlData += kmlPlacemarkData;
                            }
                        }

                        kmlData += "</Folder>\n";
                    }

                    if (fuentesCoords?.length > 0) {
                        kmlData += `<Folder>
      <name>${t("mapconfig.fuentes")}</name>\n`;

                        for (const fuente of fuentesCoords) {
                            const kmlPlacemarkData = `<Placemark>
        <name>${escapeXML(fuente.nombre)}</name>
        <Point>
        <coordinates>${fuente.position.y},${fuente.position.x},0</coordinates>
        </Point>
        </Placemark>\n`;
                            kmlData += kmlPlacemarkData;
                        }

                        kmlData += "</Folder>\n";
                    }

                    kmlData += `</Document>
      </kml>`;

                    const blob = new Blob([kmlData], { type: filetype });
                    const elem = window.document.createElement("a");
                    elem.href = window.URL.createObjectURL(blob);
                    elem.download = filename;
                    document.body.appendChild(elem);
                    elem.click();
                    document.body.removeChild(elem);
                }
            }
        } catch (err) {
            toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
        }
    };
    return (
        <div className='row-container'>
            {isLoaded === false && <LoadingDialog />}
            <div className='row'>
                <div className="col-xl-6 col-lg-12 col-sm-12" >
                    <FormikForm
                        canEdit={canEdit}
                        farmId={farmId}
                        dialogState={dialogState}
                        setDialogState={setDialogState}
                        data={dataFarm} isLoaded={isLoaded}
                        updateData={(newData) => callSend(`/farms/${farmId}/general`, dataFarm, newData, t, setDataFarm)}
                        redirDC={redirDC}
                        adminUsers={adminUsers}
                    />
                </div>
                <div className="col-xl-6 col-lg-12 col-sm-12 map-col">
                    <div className="card">
                        <div className="card-body">
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-start align-items-center' />
                                <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                    <h5 className="card-title">
                                        <span>{t("maps.map")}</span>
                                        {tooltipVisible ?
                                            <TooltipPrime id="farmInfo_map-no-data" content={t("maps.nodata")} >
                                                <FontAwesomeIcon  size="1x" icon={faInfoCircle} style={{ color: "tomato" }} />
                                            </TooltipPrime>
                                            : null}
                                    </h5>
                                </div>
                                <div className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center'>
                                    {enableDownloadKML &&
                      <TooltipPrime id="farmInfo_map-export" content={t("exportMap")} position="bottom" >
                          <FontAwesomeIcon className='pointer fa-regular' size="2x" icon={faFileExport} style={{ color: "grey", fontSize:"1.5rem", marginRight:"10px" }} onClick={exportToKML} />
                      </TooltipPrime>}
                                    <TooltipPrime id="farmInfo_map-redirect" content={t("mapConfigTooltip")} position="left" >
                                        <FontAwesomeIcon className='pointer' onClick={() => redirectToMapaConfig()} size="2x" icon={faFileImport} style={{ color: "grey", fontSize:"1.5rem" }} />
                                    </TooltipPrime>
                                </div>
                            </div>
                            <div className='linea-titulo' />
                            <div className='mapsWrapper'>
                                <GoogleMapsFarm
                                    apiKey="AIzaSyCvs7xPDh2vfmQO2w02lcItOXa6OKOVmQ4"
                                    data={dataFarm.mapa_json}
                                    farmId={dataFarm.campo_id}
                                    innerRef={mapRef}
                                    language={i18n.language}
                                    iconBorder={farmblack}
                                    husoHorario={dataFarm.huso_horario}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-sm-12 col-lg-12">
                    <div className="card">
                        <div className="card-body">
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-4'>
                                    <SelfHelp t={t} params={selfHelpParams} images={[SelfHelpFarminfo1, SelfHelpFarminfo2]} lng={i18n.language} zonesTableEditable />
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                    <h5 className="card-title">
                                        <span>{`${t("farms.zonesTable.title")} (${(Array.isArray(sectorsInfo) && sectorsInfo.length) || 0})`}</span>
                                    </h5>
                                </div>
                                <div id="zonesTableToolbar"
                                    className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center'
                                />
                            </div>
                            <div className='linea-titulo' />

                            {(Array.isArray(sectorsInfo) && sectorsInfo.length > 0) ?
                                <ZonesTable dataFarm={dataFarm} dispatch={dispatch} canEdit={canEdit} sectorsInfo={sectorsInfo}
                                    equiposInfo={equiposDict} isFarmInfo t={t} i18n={i18n} location={location} units={units}
                                />
                                :
                                <span className='no-data'>{t("forms.noData")}</span>}
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className='col col-sm-2 col-md-3 col-lg-4'>
                                    <SelfHelp t={t} params={selfHelpParamsNet} images={[selfHelpNetImg1, selfHelpNetImg2]} lng={i18n.language} simpleEditTable />
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                    <h5 className="card-title">
                                        <span>{t("farms.networkTable.title")}</span>
                                    </h5>
                                </div>
                                <div className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center' />
                            </div>
                            <div className='linea-titulo' />

                            {(Object.keys(dataFarm).length > 0 && Array.isArray(network) && network.length > 0) ?
                                <NetworkTable dataFarm={dataFarm} dispatch={dispatch} canEdit={canEdit} nodesInfo={network} mapRef={mapRef} t={t} i18n={i18n} />
                                :
                                <span className='no-data'>{t("forms.noData")}</span>}
                        </div>
                    </div>
                    {
                        dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado &&
            <div className="card">
                <div className="card-body">
                    <div className="row">
                        <div className='col col-sm-2 col-md-3 col-lg-4'>
                            <SelfHelp t={t} target="PhenologyTable" images={phenologyHelpImages} lng={i18n.language} />
                        </div>
                        <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                            <h5 className="card-title">
                                <span>{t("farms.fenologia.title")}</span>
                            </h5>
                        </div>
                        <div className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center'>
                            <FontAwesomeIcon className='btnEdit pointer text-dark' size="lg" icon={faPlus}
                                onClick={(event) => {
                                    event.preventDefault();
                                    const newSearch = `?farm=${dataFarm.campo_id}`;
                                    navigate({ pathname: "/farms/phenology", search: newSearch });
                                }}
                            />
                        </div>
                    </div>
                    <div className='linea-titulo' />
                    {(Array.isArray(plantillasFenologia) && plantillasFenologia.length > 0) ?
                        <PhenologyTable dataFarm={dataFarm} t={t} i18n={i18n}   />
                        :
                        <span className='no-data'>{t("forms.noData")}</span>}
                </div>
            </div>
                    }
                    {
                        loadedDatosFact?
                            <>
                                <Formik innerRef={formikRef}
                                    initialValues={{
                                        "campo_id":dataFarm.campo_id,
                                        "datos_fact":billingData
                                    }}
                                    validate={(data) => {
                                        const errors = {};
                                        if(!data?.datos_fact?.nombre){
                                            errors.datos_fact_nombre = "Required";
                                        }
                                        if(!data?.datos_fact?.direccion){
                                            errors.datos_fact_direccion = "Required";
                                        }
                                        if(!data?.datos_fact?.ciudad){
                                            errors.datos_fact_ciudad = "Required";
                                        }
                                        if(!data?.datos_fact?.tipo_pais_id){
                                            errors.datos_fact_tipo_pais_id = "Required";
                                        }
                                        const requiredVat = countries?.find(c => c?.tipo_pais_id === data?.datos_fact?.tipo_pais_id)?.required_vat;
                                        if(requiredVat && !data?.datos_fact?.vat_id){
                                            errors.datos_fact_vat_id = "Required";
                                        }
                                        return errors;
                                    }}
                                    onSubmit={async (formData) => {
                                        setOnSendDatosFact(true);
                                        RestAPI.put("apiAdminDropControl", "/billing/billingparams", {body: {"datos_fact":{...formData.datos_fact}, "type":"datosFact", "campo_id":dataFarm.campo_id}}).then(async (response) => {
                                            toast.success(t("billing.billingparamsSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
                                            const index = billingInfoFarm?.datos_fact_list?.findIndex(d => d.datos_fact_id === response?.newDatosFact?.[0].datos_fact_id);
                                            if (index === -1) {
                                                setNewDatosFact(response?.newDatosFact?.[0]);
                                                formikRef.current && formikRef.current.setFieldValue("datos_fact",response?.newDatosFact?.[0]);
                                                setBillingData(response?.newDatosFact?.[0]);
                                            }else{
                                                billingInfoFarm.datos_fact_list[index] =response?.newDatosFact?.[0];
                                                billingInfoFarm.datos_fact =response?.newDatosFact?.[0];
                                                billingInfoFarm.datos_fact_cli =response?.newDatosFact?.[0];
                                                formikRef.current && formikRef.current.setFieldValue("datos_fact",response?.newDatosFact?.[0]);
                                                setBillingData(response?.newDatosFact?.[0]);
                                            }
                                            setOnSendDatosFact(false);
                                            setOnEditFact(false);
                                        })
                                            .catch((error) => {
                                                setOnSendDatosFact(false);
                                                console.log(error);
                                                if(error?.response?.data === "MISSING_FACT_DATA"){
                                                    toast.error(t("billing.errorMissingData"), { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
                                                }
                                                else if(error?.response?.data === "MISSING_VAT"){
                                                    toast.error(t("billing.errorMissingVat"), { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
                                                }
                                                setOnEditFact(true);
                                                toast.error(t("billing.billingparamsError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
                                            });
                                    }}
                                >
                                    {({handleSubmit, values, setFieldValue, errors}) => (
                                        <div className='row'>
                                            <div className="col-xl-12 col-lg-12 col-sm-12">
                                                {
                                                    onSendDatosFact?
                                                        <LoadingDialog />
                                                        :
                                                        <DatosFact
                                                            farmInfo={billingInfoFarm} countries={countries} noEditable={!onEditFact}
                                                            values={values} setFieldValue={setFieldValue} header ownEditBtns
                                                            handleSubmit={handleSubmit} setEditable={setOnEditFact} formikRef={formikRef} errors={errors}
                                                            originalValues={originalValues} setOriginalValues={setOriginalValues} newDatosFactList={newDatosFactList} setNewDatosFactList={setNewDatosFactList}
                                                            newDatosFact={newDatosFact} setNewDatosFact={setNewDatosFact} setBillingInfoFarm={setBillingInfoFarm} validate={validate} setValidate={setValidate}
                                                        />
                                                }
                                            </div>
                                        </div>
                                    )}
                                </Formik>
                                {
                                    billingInfoFarm?.features_dropcontrol?.billing?
                                        <BillingParamsPanel billingInfo={billingInfoFarm} />
                                        :null
                                }
                            </>
                            :null
                    }
                </div>
            </div>
        </div>
    );
};

export default withName(FarmInfo, "FarmFrag");
